/* =========================================================================
   Bundled Get-To-Know-You bank (guest-safe). The entire /api/gtky group
   requires auth (GtkyEndpoints.cs), so guests cannot reach the server bank —
   this bundled set (dykData.ts pattern) gives everyone the Theo-conversation
   moment and populates the local memory cache. Each prompt carries a MemoryKey
   used to condition character lines. Mirrors app.GTKYQuestions (db/001:736-747
   for T1 + the two legacy keys, db/049 for T2/T3).

   ASK ORDER IS ARRAY ORDER. pickGtkyQuestion() walks this list top-to-bottom
   and serves the first key with nothing cached, so the tiers below are the
   cadence: T1 warm first, then T2 engagement, then T3 spiritual-life-lite
   (PLAN-gameplay-v2 §6).

   HOUSE RULE — NO KEY WITHOUT A CONSUMER. Every entry below names, in a
   comment, what actually reads the answer: a Theo memory line (characters/
   theo.ts `requiresMemoryKey`), ContentVariants targeting (RequiresGtkyKey /
   RequiresGtkyValue, db/031, authored in /content-studio), or a serving /
   scheduling preference. If a key has no consumer it does not belong here.

   REVERENCE POLICY. T3 stops at devotional preference. Nothing about sin,
   confession, marital state, health, finances or parish politics — ever. Every
   question is skippable ("Maybe later") and every question carries "Prefer not
   to say", which retires it permanently (gtky.ts recordGtkyDecline).
   ========================================================================= */
export type GtkyAnswerType = 'single' | 'multi' | 'bool' | 'text';

export interface GtkyQuestion {
  key: string; // MemoryKey — localStorage['catholics.gtky.<key>']
  prompt: string;
  answerType: GtkyAnswerType;
  options?: string[];
}

export const GTKY_STARTER: readonly GtkyQuestion[] = [
  /* ---------------------------------------------------------------------- */
  /* T1 — warm (v1.1.0 starter set; already live in app.GTKYQuestions)       */
  /* ---------------------------------------------------------------------- */
  {
    // SPECIAL: the first-name ask (v1.1.0). Driven by gtky.ts triggerNameAsk() on
    // the first home visit — NOT by the generic pickGtkyQuestion() rotation (which
    // skips this key). Listed here so it shares the overlay/memory machinery and
    // its MemoryKey is documented. Cached at localStorage['catholics.gtky.player_name'].
    // CONSUMER: {name} interpolation in every Theo line (characters/name.ts).
    key: 'player_name',
    prompt: 'Before we set out — what should I call you?',
    answerType: 'text',
  },
  {
    // CONSUMER: Theo 'feast-intro' memory line (value 'Yes').
    key: 'visited_rome',
    prompt: 'Have you ever been to the Vatican?',
    answerType: 'single',
    options: ['Yes', 'Not yet — on my list!', 'No'],
  },
  {
    // CONSUMER: ContentVariants targeting — convert/exploring segments get
    // gentler email + push copy (/content-studio, db/031).
    key: 'faith_journey',
    prompt: 'How did you come to the faith?',
    answerType: 'single',
    options: ['Cradle Catholic', 'Convert to Catholicism', 'Non-Catholic who loves this game', 'Exploring'],
  },
  {
    // CONSUMER: ContentVariants targeting — feast/holy-day nudges are written
    // for the "Holidays"/"Not currently" segments, not sent to daily communicants.
    key: 'mass_frequency',
    prompt: 'How often do you attend Mass?',
    answerType: 'single',
    options: ['Daily', 'Weekly', 'Monthly', 'Holidays', 'Not currently'],
  },
  {
    // CONSUMER: Theo 'cameo' memory line ("May your favourite saint pray for you…").
    key: 'favorite_saint',
    prompt: 'Do you have a favorite saint?',
    answerType: 'text',
  },
  {
    // CONSUMER: prayer-break framing + ContentVariants targeting for Rosary content.
    key: 'prayed_rosary',
    prompt: 'Have you prayed the Rosary this week?',
    answerType: 'bool',
  },
  {
    // CONSUMER: round-picker tile ordering (serving preference) + later serving bias.
    key: 'topics_of_interest',
    prompt: 'Which topics interest you most?',
    answerType: 'multi',
    options: ['Bible', 'Saints', 'Church History', 'Doctrine', 'Liturgy', 'Popes', 'Mary'],
  },
  {
    // CONSUMER: gates the Chosen content wave — 'Not yet' suppresses show-specific
    // questions and every S4/S5 spoiler variant (serving preference).
    key: 'watched_chosen',
    prompt: 'Have you watched The Chosen?',
    answerType: 'single',
    options: ['Every episode!', 'Some of it', 'Not yet'],
  },
  {
    // CONSUMER: ContentVariants targeting — seasonal email/push copy picks the
    // player's own favourite season for its warm opener.
    key: 'liturgical_season',
    prompt: "What's your favorite liturgical season?",
    answerType: 'single',
    options: ['Advent', 'Christmas', 'Lent', 'Easter', 'Ordinary Time'],
  },
  {
    // Legacy key: already seeded in app.GTKYQuestions (db/001) + tagged (db/018)
    // but never in the bundled bank. CONSUMER: ContentVariants targeting for
    // family/kids content and the "teach the faith at home" email track.
    key: 'has_children',
    prompt: 'Do you have children learning the faith?',
    answerType: 'bool',
  },
  {
    // Legacy key (db/001 + db/018). CONSUMER: the daily saint push topic —
    // notification preferences (db/028/045) read this as the opt-in signal.
    key: 'wants_saint_reminder',
    prompt: 'Would you like a daily saint reminder?',
    answerType: 'bool',
  },

  /* ---------------------------------------------------------------------- */
  /* T2 — engagement (PLAN-gameplay-v2 §6; server rows added by db/049)      */
  /* ---------------------------------------------------------------------- */
  {
    // CONSUMER: ContentVariants targeting for the Chosen content wave —
    // character-specific facts, questions and email openers (/content-studio).
    key: 'chosen_character',
    prompt: 'Who is your favorite character in The Chosen?',
    answerType: 'single',
    options: ['Jesus', 'Simon Peter', 'Mary Magdalene', 'Matthew', 'Nicodemus', 'Mother Mary', 'Someone else'],
  },
  {
    // CONSUMER: SPOILER GATE (serving preference) — S4/S5 questions and DYK
    // facts are withheld unless the player says they have watched that far.
    key: 'chosen_seasons',
    prompt: 'How far along are you in The Chosen?',
    answerType: 'single',
    options: ['Season 1', 'Through Season 2', 'Through Season 3', 'Through Season 4', 'All caught up', 'Avoid spoilers, please'],
  },
  {
    // CONSUMER: send-time personalization for the engagement push (db/045) and
    // email cadence — we knock when the player is actually awake.
    key: 'play_time',
    prompt: 'When do you usually play?',
    answerType: 'single',
    options: ['Morning', 'Lunchtime', 'Afternoon', 'Evening', 'Late night', 'It varies'],
  },
  {
    // CONSUMER: the hard-mode on-ramp (PLAN-gameplay-v2 §3b) — a 'Yes' turns the
    // stretch-question taste into an explicit hard-round invitation. Also a Theo
    // 'stretch-win' memory line (2 authored) so the answer pays back immediately.
    key: 'wants_harder',
    prompt: 'Would you like me to send tougher questions your way?',
    answerType: 'single',
    options: ['Yes — challenge me', 'Now and then', 'No, this is just right'],
  },
  {
    // CONSUMER: round-picker featured tile (serving preference) — topics_of_interest
    // orders the tiles, this single favourite is the one that gets promoted.
    key: 'favorite_category',
    prompt: 'If you could only study one subject, which would it be?',
    answerType: 'single',
    options: ['Bible', 'Saints', 'Church History', 'Doctrine', 'Liturgy', 'Popes', 'Mary'],
  },
  {
    // CONSUMER: Friendly Play invitations (friendlyTheo.ts) — "with family"
    // players are the ones offered the no-clock study mode as a shared activity.
    key: 'play_context',
    prompt: 'Do you play on your own, or with someone?',
    answerType: 'single',
    options: ['On my own', 'With my spouse', 'With my kids', 'With a class or group', 'It varies'],
  },
  {
    // CONSUMER: break cadence + round-length suggestion (PLAN-gameplay-v2 §3a) —
    // "a few minutes" players get shorter runs and fewer mini-game breaks.
    key: 'session_length',
    prompt: 'How long is a good session for you?',
    answerType: 'single',
    options: ['A few minutes', 'About fifteen minutes', 'Half an hour or more'],
  },

  /* ---------------------------------------------------------------------- */
  /* T3 — spiritual-life-lite (PLAN §6; devotional PREFERENCE only, nothing  */
  /* more intimate — see the reverence policy in the header)                 */
  /* ---------------------------------------------------------------------- */
  {
    // CONSUMER: Theo 'daily' memory lines (2 authored) + ContentVariants
    // targeting for devotion-specific prayer-break and email content.
    key: 'favorite_devotion',
    prompt: 'Do you have a devotion you especially love?',
    answerType: 'single',
    options: [
      'The Rosary',
      'Divine Mercy',
      'Eucharistic Adoration',
      'The Sacred Heart',
      'Lectio Divina',
      'The Liturgy of the Hours',
      'Still finding mine',
    ],
  },
  {
    // CONSUMER: Theo 'greeting-evening' memory lines (2 authored) + prayer-break
    // content selection (the Rosary break opens on the player's own mystery).
    key: 'favorite_mystery',
    prompt: 'Which Mystery of the Rosary stays with you most?',
    answerType: 'single',
    options: ['Joyful', 'Luminous', 'Sorrowful', 'Glorious'],
  },
  {
    // CONSUMER: Theo 'rankup' memory lines (2 authored) + feast-day
    // personalization (their saint's feast gets a warm note).
    key: 'confirmation_saint',
    prompt: 'Which saint did you take at Confirmation?',
    answerType: 'text',
  },
  {
    // CONSUMER: ContentVariants targeting — volunteer/catechist segments get
    // teaching-oriented content; "just visiting" segments never get a volunteer ask.
    key: 'parish_involvement',
    prompt: 'How involved are you at your parish these days?',
    answerType: 'single',
    options: ['I serve or volunteer', 'I attend regularly', 'I attend now and then', 'Between parishes', 'Just visiting the faith'],
  },
];
